import React, { useState } from 'react';
import { 
  Activity, Clock, User, CheckCircle2, Search, Filter, Plus, 
  FileText, ShieldCheck, Stethoscope, Sparkles, AlertCircle, Printer
} from 'lucide-react';
import { INITIAL_TREATMENT_AUDIT_LOGS, TreatmentSessionLog } from '../data/treatmentAuditStore';
import { DEMO_STUDENTS } from '../data/demoStudents';

export const TreatmentAuditTracker: React.FC = () => {
  const [auditLogs, setAuditLogs] = useState<TreatmentSessionLog[]>(INITIAL_TREATMENT_AUDIT_LOGS);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // New Log Form State
  const [studentId, setStudentId] = useState(DEMO_STUDENTS[0].id);
  const [moduleKey, setModuleKey] = useState('aac');
  const [moduleName, setModuleName] = useState('01. AAC Speech Synthesizer');
  const [therapistName, setTherapistName] = useState('Sarah Jenkins (SLP)');
  const [durationMins, setDurationMins] = useState(45);
  const [scoreAchieved, setScoreAchieved] = useState('88% Goal Mastery');
  const [observation, setObservation] = useState('');
  const [cptCode, setCptCode] = useState('CPT 92507 (Speech Therapy)');
  const [status, setStatus] = useState<'ACTIVE_SESSION' | 'COMPLETED'>('COMPLETED');

  const handleAddSession = (e: React.FormEvent) => {
    e.preventDefault();
    const st = DEMO_STUDENTS.find(s => s.id === studentId) || DEMO_STUDENTS[0];

    const newLog: TreatmentSessionLog = {
      id: `LOG-2026-00${auditLogs.length + 1}`,
      studentId: st.id,
      studentName: st.name,
      rollId: st.rollId,
      moduleKey: moduleKey,
      moduleName: moduleName,
      therapistName: therapistName,
      sessionDate: new Date().toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' }),
      durationMins: durationMins,
      status: status,
      scoreAchieved: scoreAchieved,
      treatmentObservation: observation || 'Clinical treatment session completed cleanly.',
      cptCode: cptCode
    };

    setAuditLogs([newLog, ...auditLogs]);
    setIsAddModalOpen(false);
    setObservation('');
  };

  const filteredLogs = auditLogs.filter(log => {
    const matchesSearch = log.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          log.rollId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          log.moduleName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          log.therapistName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || log.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const activeSessionsCount = auditLogs.filter(l => l.status === 'ACTIVE_SESSION').length;
  const completedSessionsCount = auditLogs.filter(l => l.status === 'COMPLETED').length;

  return (
    <div className="space-y-6 max-w-7xl mx-auto py-2">
      
      {/* Clarvos Obsidian Header Summary Bento */}
      <div className="bg-[#1c1b1f] text-white p-8 md:p-10 rounded-[32px] shadow-2xl border border-white/10 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-[#ffd64e]/10 rounded-full blur-3xl pointer-events-none" />

        <div className="space-y-2 text-center md:text-left relative z-10">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 bg-[#ffd64e] text-black text-[11px] font-black rounded-full uppercase tracking-wider">
            <Activity className="w-3.5 h-3.5 text-black" />
            <span>Clinical Treatment Telemetry Log</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-black text-white tracking-tight">
            Active Therapy Session Audit Tracker
          </h2>
          <p className="text-xs md:text-sm text-slate-300 max-w-2xl font-normal">
            Real-time longitudinal tracking of multi-disciplinary clinical treatments across all AbilityOS specialized modules.
          </p>
        </div>

        <div className="flex items-center gap-3 bg-white/10 p-4 rounded-2xl border border-white/15 backdrop-blur-md shrink-0 relative z-10">
          <div className="text-center px-4 border-r border-white/10">
            <div className="text-2xl font-black text-[#ffd64e] font-mono animate-pulse">{activeSessionsCount}</div>
            <div className="text-[10px] text-white/70 font-bold uppercase tracking-wider">In-Session</div>
          </div>
          <div className="text-center px-4">
            <div className="text-2xl font-black text-[#bfecc7] font-mono">{completedSessionsCount}</div>
            <div className="text-[10px] text-white/70 font-bold uppercase tracking-wider">Completed</div>
          </div>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="px-5 py-2.5 bg-[#ffd64e] hover:bg-[#f5cb3a] text-black font-black text-xs rounded-full shadow-md transition flex items-center gap-1.5 ml-2 cursor-pointer"
          >
            <Plus className="w-4 h-4 text-black" />
            <span>Record Session</span>
          </button>
        </div>
      </div>

      {/* Filter & Search Controls (Clarvos Pill Ergonomics) */}
      <div className="bg-[#f9f6f0] p-4 rounded-[24px] border border-[#1c1b1f]/10 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md w-full">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search student, module, therapist, or CPT code..."
            className="w-full pl-10 pr-4 py-2 bg-white border border-[#1c1b1f]/10 rounded-full text-xs font-bold focus:ring-2 focus:ring-[#ffd64e] focus:outline-none"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <span className="text-xs font-bold text-slate-600">Status:</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-white border border-[#1c1b1f]/10 rounded-full text-xs font-bold text-slate-800 cursor-pointer"
          >
            <option value="ALL">All Session Statuses</option>
            <option value="ACTIVE_SESSION">Active In-Session</option>
            <option value="COMPLETED">Completed Sessions</option>
          </select>
        </div>
      </div>

      {/* Audit Log Table in Clarvos Bento Card */}
      <div className="bg-white rounded-[28px] border border-[#1c1b1f]/10 shadow-sm overflow-hidden p-6 space-y-4">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#1c1b1f] text-white/90 text-[11px] font-black uppercase tracking-wider">
                <th className="p-3.5 rounded-l-2xl">Session ID / Date</th>
                <th className="p-3.5">Student Name</th>
                <th className="p-3.5">Module Used</th>
                <th className="p-3.5">Therapist</th>
                <th className="p-3.5">Duration</th>
                <th className="p-3.5">Status</th>
                <th className="p-3.5">Score / Observation</th>
                <th className="p-3.5 rounded-r-2xl">CPT Code</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/5 text-xs">
              {filteredLogs.map((log) => (
                <tr key={log.id} className="hover:bg-[#eae7e1]/30 transition">
                  <td className="p-3.5">
                    <div className="font-mono font-bold text-slate-800">{log.id}</div>
                    <div className="text-[10px] text-slate-500">{log.sessionDate}</div>
                  </td>

                  <td className="p-3.5">
                    <div className="font-bold text-slate-900">{log.studentName}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{log.rollId}</div>
                  </td>

                  <td className="p-3.5 font-semibold text-blue-700">
                    {log.moduleName}
                  </td>

                  <td className="p-3.5 font-medium text-slate-700">
                    {log.therapistName}
                  </td>

                  <td className="p-3.5 font-mono font-bold text-slate-800">
                    {log.durationMins} Min
                  </td>

                  <td className="p-3.5">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold inline-flex items-center gap-1.5 ${
                      log.status === 'ACTIVE_SESSION' 
                        ? 'bg-amber-100 text-amber-900 border border-amber-300 animate-pulse' 
                        : 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                    }`}>
                      <span className={`w-2 h-2 rounded-full ${log.status === 'ACTIVE_SESSION' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                      {log.status.replace('_', ' ')}
                    </span>
                  </td>

                  <td className="p-3.5 space-y-0.5">
                    <div className="font-bold text-emerald-700">{log.scoreAchieved}</div>
                    <div className="text-[11px] text-slate-600 line-clamp-1">{log.treatmentObservation}</div>
                  </td>

                  <td className="p-3.5 font-mono text-[10px] font-bold text-slate-600">
                    {log.cptCode || 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add New Session Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1c1b1f]/80 backdrop-blur-md">
          <div className="bg-[#f9f6f0] rounded-[32px] shadow-2xl border border-black/10 w-full max-w-lg overflow-hidden">
            <div className="bg-[#1c1b1f] p-6 text-white flex justify-between items-center">
              <h3 className="font-black text-base flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#ffd64e]" />
                Record Live Clinical Treatment Session
              </h3>
              <button onClick={() => setIsAddModalOpen(false)} className="text-white/60 hover:text-white p-1 rounded-full"><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={handleAddSession} className="p-6 space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Select Patient / Student *</label>
                <select
                  value={studentId}
                  onChange={(e) => setStudentId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-xl font-bold text-slate-800 focus:ring-2 focus:ring-blue-500"
                >
                  {DEMO_STUDENTS.map(st => (
                    <option key={st.id} value={st.id}>{st.name} ({st.rollId}) - {st.disabilityCategory}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Module Name</label>
                  <input
                    type="text"
                    value={moduleName}
                    onChange={(e) => setModuleName(e.target.value)}
                    className="w-full px-3 py-2 border rounded-xl font-bold"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Therapist Name</label>
                  <input
                    type="text"
                    value={therapistName}
                    onChange={(e) => setTherapistName(e.target.value)}
                    className="w-full px-3 py-2 border rounded-xl font-bold"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Duration (Minutes)</label>
                  <input
                    type="number"
                    value={durationMins}
                    onChange={(e) => setDurationMins(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border rounded-xl font-bold"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Score / Progress</label>
                  <input
                    type="text"
                    value={scoreAchieved}
                    onChange={(e) => setScoreAchieved(e.target.value)}
                    className="w-full px-3 py-2 border rounded-xl font-bold"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Treatment Observation Notes *</label>
                <textarea
                  required
                  rows={3}
                  value={observation}
                  onChange={(e) => setObservation(e.target.value)}
                  placeholder="Record patient progress, AAC selections, motor gait notes..."
                  className="w-full px-3 py-2 border rounded-xl font-medium focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <button type="button" onClick={() => setIsAddModalOpen(false)} className="px-4 py-2 bg-slate-100 text-slate-700 font-bold rounded-xl">Cancel</button>
                <button type="submit" className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow">Log Session</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
